const VERSION_CHECK_INTERVAL_MS = 60_000;
const RELOAD_GUARD_TTL_MS = 5 * 60_000;
const RELOAD_GUARD_KEY = 'tcgate:deployment-reload';
const VERSION_QUERY_KEY = '__tcgate_build';

interface DeploymentVersion {
  buildId: string;
}

interface ReloadGuard {
  buildId: string;
  requestedAt: number;
}

const isValidBuildId = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= 128;

const readReloadGuard = (): ReloadGuard | null => {
  try {
    const value = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!value) return null;

    const parsed = JSON.parse(value) as Partial<ReloadGuard>;
    return isValidBuildId(parsed.buildId) && typeof parsed.requestedAt === 'number'
      ? { buildId: parsed.buildId, requestedAt: parsed.requestedAt }
      : null;
  } catch {
    return null;
  }
};

const clearSuccessfulReload = (deploymentConfirmed = false) => {
  const guard = readReloadGuard();
  if (deploymentConfirmed || guard?.buildId === __TCGATE_BUILD_ID__) {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  }

  const currentUrl = new URL(window.location.href);
  const requestedBuild = currentUrl.searchParams.get(VERSION_QUERY_KEY);
  if (requestedBuild && (deploymentConfirmed || requestedBuild === __TCGATE_BUILD_ID__)) {
    currentUrl.searchParams.delete(VERSION_QUERY_KEY);
    window.history.replaceState(window.history.state, '', currentUrl);
  }
};

const reloadOnce = (buildId: string) => {
  const now = Date.now();
  const guard = readReloadGuard();
  if (guard?.buildId === buildId && now - guard.requestedAt < RELOAD_GUARD_TTL_MS) return;

  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify({ buildId, requestedAt: now }));
  } catch {
    // A reload still works when storage is unavailable (for example, strict private browsing).
  }

  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set(VERSION_QUERY_KEY, buildId);
  window.location.replace(nextUrl);
};

const fetchDeploymentVersion = async (signal: AbortSignal): Promise<DeploymentVersion | null> => {
  const versionUrl = new URL(`${import.meta.env.BASE_URL}version.json`, window.location.origin);
  versionUrl.searchParams.set('t', Date.now().toString());

  try {
    const response = await fetch(versionUrl, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) return null;

    const value = await response.json() as Partial<DeploymentVersion>;
    return isValidBuildId(value.buildId) ? { buildId: value.buildId } : null;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return null;
    return null;
  }
};

export const setupDeploymentUpdates = () => {
  if (!import.meta.env.PROD) return () => undefined;

  clearSuccessfulReload();
  const controller = new AbortController();
  let checking = false;

  const checkForUpdate = async () => {
    if (checking || document.visibilityState === 'hidden') return;

    checking = true;
    try {
      const deployment = await fetchDeploymentVersion(controller.signal);
      if (!deployment) return;

      if (deployment.buildId === __TCGATE_BUILD_ID__) {
        clearSuccessfulReload(true);
        return;
      }

      reloadOnce(deployment.buildId);
    } finally {
      checking = false;
    }
  };

  const checkWhenVisible = () => {
    if (document.visibilityState === 'visible') void checkForUpdate();
  };
  const intervalId = window.setInterval(() => void checkForUpdate(), VERSION_CHECK_INTERVAL_MS);

  window.addEventListener('focus', checkForUpdate);
  window.addEventListener('online', checkForUpdate);
  document.addEventListener('visibilitychange', checkWhenVisible);
  void checkForUpdate();

  return () => {
    controller.abort();
    window.clearInterval(intervalId);
    window.removeEventListener('focus', checkForUpdate);
    window.removeEventListener('online', checkForUpdate);
    document.removeEventListener('visibilitychange', checkWhenVisible);
  };
};

export const setupStaleChunkRecovery = () => {
  const handlePreloadError = (event: Event) => {
    event.preventDefault();
    reloadOnce(`stale-${__TCGATE_BUILD_ID__}`);
  };

  window.addEventListener('vite:preloadError', handlePreloadError);
  return () => window.removeEventListener('vite:preloadError', handlePreloadError);
};
